import React from "react";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import Chat from "../components/Chat";
import Feed from "../components/Feed";
import Inbox from "../components/Inbox";
import MapContainer from "../components/Map/MapContainer";
import MapPicker from "../components/Map/LocationPickerMap";
import PageLayout from "../components/PageLayout";
import AdminAnalytics from "../pages/AdminAnalytics";
import Fishipedia from "../pages/Fishipedia";
import FishipediaRankings from "../pages/FishipediaRankings";
import Feedback from "../pages/Feedback";
import FavoriteSpotsPage from "../pages/FavoriteSpotsPage";
import FishingRegulations from "../pages/FishingRegulations";
import FishingToday from "../pages/FishingToday";
import FishingSchool from "../pages/FishingSchool";
import GearDeals from "../pages/GearDeals";
import Groups from "../pages/Groups";
import LegalDocumentPage from "../pages/LegalDocumentPage";
import AuthCallback from "../pages/AuthCallback";
import ForgotPassword from "../pages/ForgotPassword";
import Login from "../pages/Login";
import ReelitProLandingPage from "../components/landing/ReelitProLandingPage";
import MyGearPage from "../pages/MyGearPage";
import MyTripsPage from "../pages/MyTripsPage";
import News from "../pages/News";
import OAuthProfileCompletion from "../pages/OAuthProfileCompletion";
import Records from "../pages/Records";
import PublicSeoLandingPage from "../pages/PublicSeoLandingPage";
import Register from "../pages/Register";
import ResetPassword from "../pages/ResetPassword";
import Support from "../pages/Support";
import SupportApp from "../pages/SupportApp";
import SuggestedAnglers from "../pages/SuggestedAnglers";
import SupabaseClaimTool from "../pages/SupabaseClaimTool";
import Surveys from "../pages/Surveys";
import UserManual from "../pages/UserManual";
import bgVideo from "../assets/Never_Ending_Background_Video_Request.mp4";
import {
  allowComingSoonRoutes,
  allowGearDealsRoute,
  allowRegulationsRoute,
} from "../config/featureVisibility";
import {
  createFavoriteSpotOutlookContext,
  favoriteSpotMapEventDetail,
} from "../features/favoriteSpots/favoriteSpotOutlookContext";
import { FISHING_OUTLOOK_ACCESS_STATE } from "../features/fishingOutlook/useFishingOutlookBetaAccess";
import { isSimpleModeHiddenPath } from "../utils/appMode";
import "../styles/components/AppMode.css";

function SimpleModeHiddenFeature({ t, onSwitchToFullMode }) {
  const navigate = useNavigate();

  return (
    <section className="simple-mode-hidden" aria-live="polite">
      <div className="simple-mode-hidden__panel">
        <p className="simple-mode-hidden__eyebrow">
          {t("simple_mode", { defaultValue: "Simple Mode" })}
        </p>
        <h1>
          {t("simple_mode_hidden_title", {
            defaultValue: "This feature is hidden in Simple Mode",
          })}
        </h1>
        <p>
          {t("simple_mode_hidden_body", {
            defaultValue:
              "To keep ReelitPro easier to use, this feature is hidden while Simple Mode is on. Switch to Full Mode to open it.",
          })}
        </p>
        <div className="simple-mode-hidden__actions">
          <button
            type="button"
            className="simple-mode-hidden__primary"
            onClick={onSwitchToFullMode}
          >
            {t("switch_to_full_mode", { defaultValue: "Switch to Full Mode" })}
          </button>
          <button
            type="button"
            className="simple-mode-hidden__secondary"
            onClick={() => navigate("/", { replace: true })}
          >
            {t("back_to_home", { defaultValue: "Back to Home" })}
          </button>
        </div>
      </div>
    </section>
  );
}

function FeatureUnavailable({ t }) {
  const navigate = useNavigate();

  return (
    <section className="simple-mode-hidden" aria-live="polite">
      <div className="simple-mode-hidden__panel">
        <p className="simple-mode-hidden__eyebrow">
          {t("feature_unavailable_eyebrow", { defaultValue: "Coming Soon" })}
        </p>
        <h1>
          {t("feature_unavailable_title", {
            defaultValue: "This feature is not available yet",
          })}
        </h1>
        <p>
          {t("feature_unavailable_body", {
            defaultValue:
              "This area is still being prepared and is not visible on production yet.",
          })}
        </p>
        <div className="simple-mode-hidden__actions">
          <button
            type="button"
            className="simple-mode-hidden__secondary"
            onClick={() => navigate("/", { replace: true })}
          >
            {t("back_to_home", { defaultValue: "Back to Home" })}
          </button>
        </div>
      </div>
    </section>
  );
}

export default function AppRoutes({
  t,
  activeTab,
  setActiveTab,
  setShowSidebar,
  session,
  postsComposer,
  messaging,
  normalizeCoords,
  useDesktopMapRightPanel = false,
  mapRightPanelTargetId,
  tripsRightPanelTargetId,
  utilityRightPanelTargetId,
  onMapRightPanelActiveChange,
  onMapLocateMeHandlerChange,
  onCatchMarkerDetailsOpenChange,
  onRootTabSelect,
  onSidebarRouteNavigate,
  useModernExploreShell = false,
  isSimpleModeActive = false,
  onSwitchToFullMode,
  fishingOutlookAccessState = FISHING_OUTLOOK_ACCESS_STATE.UNAUTHORIZED,
}) {
  const location = useLocation();
  const [favoriteSpotOutlookContext, setFavoriteSpotOutlookContext] =
    React.useState(null);
  const canAccessFishingOutlook =
    fishingOutlookAccessState === FISHING_OUTLOOK_ACCESS_STATE.AUTHORIZED;
  const isSupabaseClaimToolEnabled =
    (process.env.REACT_APP_ENABLE_SUPABASE_CLAIM_TOOL || "")
      .trim()
      .toLowerCase() === "true";

  const { user, setUser, profileUi, favorites, search, actions: sessionActions } =
    session;
  const outlookOwnerRef = React.useRef(user?.id || user?.username || null);
  const {
    showProfile,
    showUserProfile,
    setShowProfile,
    setShowUserProfile,
    setSelectedUser,
    requestProfileBioEdit,
  } = profileUi;
  const { favoriteSpots, handleAddFavoriteSpot } = favorites;
  const { openGroupSearchRequest } = search;

  React.useEffect(() => {
    if (!canAccessFishingOutlook) {
      setFavoriteSpotOutlookContext(null);
    }
  }, [canAccessFishingOutlook]);

  React.useEffect(() => {
    const ownerId = user?.id || user?.username || null;
    if (outlookOwnerRef.current === ownerId) return;
    outlookOwnerRef.current = ownerId;
    setFavoriteSpotOutlookContext(null);
  }, [user?.id, user?.username]);

  React.useEffect(() => {
    setFavoriteSpotOutlookContext((current) => {
      if (!current) return current;
      const exactSpot = (Array.isArray(favoriteSpots) ? favoriteSpots : []).find(
        (spot) => spot.spotKey === current.spotKey
      );
      return exactSpot ? createFavoriteSpotOutlookContext(exactSpot) : null;
    });
  }, [favoriteSpots]);
  const {
    filteredPosts,
    posts,
    startup,
    pagination,
    info,
    composer,
    sharing,
    actions: postsActions,
  } = postsComposer;
  const {
    chatUserId,
    setChatUserId,
    pendingChatText,
    pendingChatUserName,
    showMessagesSettings,
    setShowMessagesSettings,
    messagesBackgroundMode,
    setMessagesBackgroundMode,
    isMobileMessagesViewport,
    messagesSettingsRef,
    handleCloseChat,
  } = messaging;

  const simpleModeHiddenElement = (
    <SimpleModeHiddenFeature
      t={t}
      onSwitchToFullMode={onSwitchToFullMode}
    />
  );
  const featureUnavailableElement = <FeatureUnavailable t={t} />;

  const routeElement = (path, element) =>
    isSimpleModeActive && isSimpleModeHiddenPath(path)
      ? simpleModeHiddenElement
      : element;
  const gatedRouteElement = (path, element, isAllowed) =>
    isAllowed ? routeElement(path, element) : featureUnavailableElement;
  const fishingOutlookRouteElement = (element) => {
    if (
      fishingOutlookAccessState === FISHING_OUTLOOK_ACCESS_STATE.AUTHORIZED
    ) {
      return routeElement("/fishing-today", element);
    }
    if (fishingOutlookAccessState === FISHING_OUTLOOK_ACCESS_STATE.LOADING) {
      return null;
    }
    return <Navigate to="/" replace />;
  };

  const isRootMarketHidden =
    isSimpleModeActive && isSimpleModeHiddenPath("/", location.search);

  const messagesSettingsControl = (
    <div className="messages-settings-control" ref={messagesSettingsRef}>
      <button
        type="button"
        className="messages-settings-button"
        aria-expanded={showMessagesSettings}
        onClick={() => setShowMessagesSettings((prev) => !prev)}
      >
        {'\u2699'}{'\uFE0F'} {t("settings")}
      </button>

      {showMessagesSettings && (
        <div className="messages-settings-menu">
          <button
            type="button"
            className={`messages-settings-option ${
              messagesBackgroundMode === "video" ? "active" : ""
            }`}
            aria-pressed={messagesBackgroundMode === "video"}
            onClick={() => {
              setMessagesBackgroundMode("video");
              setShowMessagesSettings(false);
            }}
          >
            {t("landing_video_background_label", {
              defaultValue: "Video background",
            })}
          </button>
          <button
            type="button"
            className={`messages-settings-option ${
              messagesBackgroundMode === "white" ? "active" : ""
            }`}
            aria-pressed={messagesBackgroundMode === "white"}
            onClick={() => {
              setMessagesBackgroundMode("white");
              setShowMessagesSettings(false);
            }}
          >
            {t("landing_white_background_label", {
              defaultValue: "White background",
            })}
          </button>
        </div>
      )}
    </div>
  );

  if (!user) {
    return (
      <Routes>
        <Route path="/" element={<ReelitProLandingPage />} />
        <Route path="/login" element={<Login setUser={setUser} />} />
        <Route path="/register" element={<Register setUser={setUser} />} />
        <Route
          path="/auth/callback"
          element={<AuthCallback setUser={setUser} />}
        />
        <Route
          path="/auth/complete-profile"
          element={<OAuthProfileCompletion setUser={setUser} />}
        />
        <Route
          path="/fishing-app"
          element={<PublicSeoLandingPage pageKey="fishingApp" />}
        />
        <Route
          path="/fishing-catch-log-app"
          element={<PublicSeoLandingPage pageKey="fishingCatchLogApp" />}
        />
        <Route
          path="/fishing-spots-app"
          element={<PublicSeoLandingPage pageKey="fishingSpotsApp" />}
        />
        <Route
          path="/fishing-trip-planner"
          element={<PublicSeoLandingPage pageKey="fishingTripPlanner" />}
        />
        <Route
          path="/fish-species-guide"
          element={<PublicSeoLandingPage pageKey="fishSpeciesGuide" />}
        />
        <Route
          path="/fishing-regulations"
          element={<PublicSeoLandingPage pageKey="fishingRegulations" />}
        />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route
          path="/legal/terms"
          element={<LegalDocumentPage documentKey="terms" fallbackPath="/login" />}
        />
        <Route
          path="/legal/privacy"
          element={<LegalDocumentPage documentKey="privacy" fallbackPath="/login" />}
        />
        <Route
          path="/legal/account-deletion"
          element={<LegalDocumentPage documentKey="accountDeletion" fallbackPath="/login" />}
        />
        <Route
          path="/legal/attributions"
          element={<LegalDocumentPage documentKey="attributions" fallbackPath="/login" />}
        />
        <Route path="*" element={<Login setUser={setUser} />} />
      </Routes>
    );
  }

  return (
    <Routes>
          <Route
            path="/"
            element={
              isRootMarketHidden ? (
                simpleModeHiddenElement
              ) : activeTab === "feed" ? (
                <Feed
                  user={user}
                  posts={filteredPosts}
                  hasMorePosts={pagination.hasMorePosts}
                  isLoadingMorePosts={pagination.isLoadingMorePosts}
                  onLoadMorePosts={pagination.loadMorePosts}
                  users={search.searchResults}
                  setShowProfile={setShowProfile}
                  setShowCreatePost={composer.setShowCreatePost}
                  setSelectedUser={setSelectedUser}
                  setShowUserProfile={setShowUserProfile}
                  setSelectedPost={info.setSelectedPost}
                  setShowInfo={info.setShowInfo}
                  setSharePost={sharing.setSharePost}
                  setSidebarOpen={setShowSidebar}
                  onDeletePost={postsActions.handleDeletePost}
                />
              ) : activeTab === "map" ? (
                <div className="map-view-container">
                  {composer.isPickingLocation ? (
                    <MapPicker />
                  ) : (
                    <MapContainer
                      activePage="map"
                      posts={posts.map((post) => ({
                        ...post,
                        catch: {
                          ...post.catch,
                          coordinates: normalizeCoords(post.catch?.coordinates),
                        },
                      }))}
                      authoritativePostRestoreResult={
                        startup.authoritativePostRestoreResult
                      }
                      viewer={user}
                      onUserUpdated={setUser}
                      onOpenProfile={(clickedUser) => {
                        if (clickedUser === user?.username) {
                          setShowProfile(true);
                        } else {
                          setSelectedUser(clickedUser);
                          setShowUserProfile(true);
                        }
                      }}
                      onOpenGroup={sessionActions.handleOpenGroupFromSearch}
                      onAddFavoriteSpot={handleAddFavoriteSpot}
                      useDesktopRightPanel={useDesktopMapRightPanel}
                      desktopRightPanelTargetId={mapRightPanelTargetId}
                      onDesktopRightPanelActiveChange={onMapRightPanelActiveChange}
                      onLocateMeHandlerChange={onMapLocateMeHandlerChange}
                      onCatchMarkerDetailsOpenChange={onCatchMarkerDetailsOpenChange}
                      onFishingOutlookContextChange={
                        canAccessFishingOutlook
                          ? setFavoriteSpotOutlookContext
                          : undefined
                      }
                      isSimpleModeActive={isSimpleModeActive}
                    />
                  )}
                </div>
              ) : activeTab === "groups" ? (
                <Groups
                  user={user}
                  users={search.searchResults}
                  openGroupRequest={openGroupSearchRequest}
                  onOpenUserProfile={sessionActions.handleOpenUserFromSearch}
                  onGroupsLoaded={search.setGroupSearchResults}
                  showProfile={showProfile}
                  showUserProfile={showUserProfile}
                  setSidebarOpen={setShowSidebar}
                />
              ) : (
                <div className="market-placeholder">
                  <h2>{t("marketplace")}</h2>
                  <p>{t("common_coming_soon")}</p>
                </div>
              )
            }
          />

          <Route
            path="/fishing-app"
            element={<PublicSeoLandingPage pageKey="fishingApp" />}
          />
          <Route
            path="/fishing-catch-log-app"
            element={<PublicSeoLandingPage pageKey="fishingCatchLogApp" />}
          />
          <Route
            path="/fishing-spots-app"
            element={<PublicSeoLandingPage pageKey="fishingSpotsApp" />}
          />
          <Route
            path="/fishing-trip-planner"
            element={<PublicSeoLandingPage pageKey="fishingTripPlanner" />}
          />
          <Route
            path="/fish-species-guide"
            element={<PublicSeoLandingPage pageKey="fishSpeciesGuide" />}
          />
          <Route
            path="/fishing-regulations"
            element={<PublicSeoLandingPage pageKey="fishingRegulations" />}
          />
          <Route
            path="/fishing-today"
            element={fishingOutlookRouteElement(
              <PageLayout
                showUserProfile={showUserProfile}
                showProfile={showProfile}
                setSidebarOpen={setShowSidebar}
                enableScrollToTop={true}
                modernShell={useModernExploreShell}
              >
                <FishingToday
                  favoriteSpotOutlookContext={favoriteSpotOutlookContext}
                  onLeaveFavoriteSpot={() => setFavoriteSpotOutlookContext(null)}
                  onOpenProfile={() => setShowProfile(true)}
                  onOpenBioEditor={() => {
                    setSelectedUser(null);
                    setShowUserProfile(false);
                    requestProfileBioEdit();
                    setShowProfile(true);
                  }}
                />
              </PageLayout>
            )}
          />

          <Route
            path="/trips"
            element={routeElement(
              "/trips",
              <PageLayout
                showUserProfile={showUserProfile}
                showProfile={showProfile}
                setSidebarOpen={setShowSidebar}
                enableScrollToTop={true}
                modernShell={useModernExploreShell}
              >
                <MyTripsPage
                  user={user}
                  posts={filteredPosts}
                  users={search.searchResults}
                  onOpenUser={sessionActions.handleOpenUserFromSearch}
                  setShowSidebar={setShowSidebar}
                  onRootTabSelect={onRootTabSelect}
                  onSidebarRouteNavigate={onSidebarRouteNavigate}
                  enableDesktopRightRail={true}
                  desktopRightRailTargetId={tripsRightPanelTargetId}
                  useShellChrome={useModernExploreShell}
                />
              </PageLayout>
            )}
          />

          <Route
            path="/my-gear"
            element={routeElement(
              "/my-gear",
              <PageLayout
                showUserProfile={showUserProfile}
                showProfile={showProfile}
                setSidebarOpen={setShowSidebar}
                enableScrollToTop={true}
                modernShell={useModernExploreShell}
              >
                <MyGearPage
                  currentUser={user}
                  posts={filteredPosts}
                  users={search.searchResults}
                  onOpenUser={sessionActions.handleOpenUserFromSearch}
                  enableDesktopRightRail={true}
                  desktopRightRailTargetId={utilityRightPanelTargetId}
                />
              </PageLayout>
            )}
          />

          <Route
            path="/favorite-spots"
            element={routeElement(
              "/favorite-spots",
              <PageLayout
                showUserProfile={showUserProfile}
                showProfile={showProfile}
                setSidebarOpen={setShowSidebar}
                enableScrollToTop={true}
                modernShell={useModernExploreShell}
              >
                <FavoriteSpotsPage
                  favoriteSpots={favoriteSpots}
                  currentUser={user}
                  posts={filteredPosts}
                  users={search.searchResults}
                  onOpenUser={sessionActions.handleOpenUserFromSearch}
                  enableDesktopRightRail={true}
                  desktopRightRailTargetId={utilityRightPanelTargetId}
                  canAccessFishingOutlook={canAccessFishingOutlook}
                  onOpenSpot={(spot) => {
                    setFavoriteSpotOutlookContext(
                      canAccessFishingOutlook
                        ? createFavoriteSpotOutlookContext(spot)
                        : null
                    );
                    setActiveTab("map");
                    setTimeout(() => {
                      window.dispatchEvent(
                        new CustomEvent("flyToSpot", {
                          detail: favoriteSpotMapEventDetail(spot),
                        })
                      );
                    }, 400);
                  }}
                />
              </PageLayout>
            )}
          />

          <Route
            path="/suggested-anglers"
            element={routeElement(
              "/suggested-anglers",
              <PageLayout
                showUserProfile={showUserProfile}
                showProfile={showProfile}
                setSidebarOpen={setShowSidebar}
                enableScrollToTop={true}
                modernShell={useModernExploreShell}
              >
                <SuggestedAnglers
                  currentUser={user}
                  users={search.searchResults}
                  posts={filteredPosts}
                  onOpenUser={sessionActions.handleOpenUserFromSearch}
                  enableDesktopRightRail={true}
                  desktopRightRailTargetId={utilityRightPanelTargetId}
                />
              </PageLayout>
            )}
          />

          <Route
            path="/fishipedia/rankings"
            element={routeElement(
              "/fishipedia/rankings",
              <PageLayout
                showUserProfile={showUserProfile}
                showProfile={showProfile}
                setSidebarOpen={setShowSidebar}
                enableScrollToTop={true}
                modernShell={useModernExploreShell}
              >
                <FishipediaRankings
                  currentUser={user}
                  posts={filteredPosts}
                  users={search.searchResults}
                  onOpenUser={sessionActions.handleOpenUserFromSearch}
                  enableDesktopRightRail={true}
                  desktopRightRailTargetId={utilityRightPanelTargetId}
                />
              </PageLayout>
            )}
          />
          <Route
            path="/fishipedia"
            element={routeElement(
              "/fishipedia",
              <PageLayout
                showUserProfile={showUserProfile}
                showProfile={showProfile}
                setSidebarOpen={setShowSidebar}
                enableScrollToTop={true}
                modernShell={useModernExploreShell}
              >
                <Fishipedia
                  currentUser={user}
                  posts={filteredPosts}
                  users={search.searchResults}
                  onOpenUser={sessionActions.handleOpenUserFromSearch}
                  enableDesktopRightRail={true}
                  desktopRightRailTargetId={utilityRightPanelTargetId}
                />
              </PageLayout>
            )}
          />
          <Route
            path="/records"
            element={gatedRouteElement(
              "/records",
              <PageLayout
                showUserProfile={showUserProfile}
                showProfile={showProfile}
                setSidebarOpen={setShowSidebar}
                modernShell={useModernExploreShell}
              >
                <Records />
              </PageLayout>,
              allowComingSoonRoutes
            )}
          />
          <Route
            path="/news"
            element={gatedRouteElement(
              "/news",
              <PageLayout
                showUserProfile={showUserProfile}
                showProfile={showProfile}
                setSidebarOpen={setShowSidebar}
                modernShell={useModernExploreShell}
              >
                <News />
              </PageLayout>,
              allowComingSoonRoutes
            )}
          />
          <Route
            path="/school"
            element={gatedRouteElement(
              "/school",
              <PageLayout
                showUserProfile={showUserProfile}
                showProfile={showProfile}
                setSidebarOpen={setShowSidebar}
                modernShell={useModernExploreShell}
              >
                <FishingSchool />
              </PageLayout>,
              allowComingSoonRoutes
            )}
          />
          {/* TODO: Re-enable Local Fishing Regulations when the regulations feature/content is completed for production. */}
          <Route
            path="/regulations"
            element={gatedRouteElement(
              "/regulations",
              <PageLayout
                showUserProfile={showUserProfile}
                showProfile={showProfile}
                setSidebarOpen={setShowSidebar}
                enableScrollToTop={true}
                modernShell={useModernExploreShell}
              >
                <FishingRegulations />
              </PageLayout>,
              allowRegulationsRoute
            )}
          />
          <Route
            path="/feedback"
            element={routeElement(
              "/feedback",
              <PageLayout
                showUserProfile={showUserProfile}
                showProfile={showProfile}
                setSidebarOpen={setShowSidebar}
                modernShell={useModernExploreShell}
              >
                <Feedback
                  currentUser={user}
                  posts={filteredPosts}
                  users={search.searchResults}
                  onOpenUser={sessionActions.handleOpenUserFromSearch}
                  enableDesktopRightRail={true}
                  desktopRightRailTargetId={utilityRightPanelTargetId}
                />
              </PageLayout>
            )}
          />
          <Route
            path="/manual"
            element={routeElement(
              "/manual",
              <PageLayout
                showUserProfile={showUserProfile}
                showProfile={showProfile}
                setSidebarOpen={setShowSidebar}
                modernShell={useModernExploreShell}
              >
                <UserManual
                  currentUser={user}
                  posts={filteredPosts}
                  users={search.searchResults}
                  onOpenUser={sessionActions.handleOpenUserFromSearch}
                  enableDesktopRightRail={true}
                  desktopRightRailTargetId={utilityRightPanelTargetId}
                />
              </PageLayout>
            )}
          />
          <Route
            path="/surveys"
            element={routeElement(
              "/surveys",
              <PageLayout
                showUserProfile={showUserProfile}
                showProfile={showProfile}
                setSidebarOpen={setShowSidebar}
                modernShell={useModernExploreShell}
              >
                <Surveys
                  currentUser={user}
                  posts={filteredPosts}
                  users={search.searchResults}
                  onOpenUser={sessionActions.handleOpenUserFromSearch}
                  enableDesktopRightRail={true}
                  desktopRightRailTargetId={utilityRightPanelTargetId}
                />
              </PageLayout>
            )}
          />
          <Route
            path="/support"
            element={
              <PageLayout
                showUserProfile={showUserProfile}
                showProfile={showProfile}
                setSidebarOpen={setShowSidebar}
                modernShell={useModernExploreShell}
              >
                <Support />
              </PageLayout>
            }
          />
          <Route
            path="/support-app"
            element={gatedRouteElement(
              "/support-app",
              <PageLayout
                showUserProfile={showUserProfile}
                showProfile={showProfile}
                setSidebarOpen={setShowSidebar}
                modernShell={useModernExploreShell}
              >
                <SupportApp
                  currentUser={user}
                  posts={filteredPosts}
                  users={search.searchResults}
                  onOpenUser={sessionActions.handleOpenUserFromSearch}
                  enableDesktopRightRail={true}
                />
              </PageLayout>,
              allowComingSoonRoutes
            )}
          />
          <Route
            path="/admin/analytics"
            element={routeElement(
              "/admin/analytics",
              <PageLayout
                showUserProfile={showUserProfile}
                showProfile={showProfile}
                setSidebarOpen={setShowSidebar}
                enableScrollToTop={true}
                modernShell={useModernExploreShell}
              >
                <AdminAnalytics />
              </PageLayout>
            )}
          />
          {isSupabaseClaimToolEnabled ? (
            <Route
              path="/migration/link-supabase"
              element={routeElement(
                "/migration/link-supabase",
                <PageLayout
                  showUserProfile={showUserProfile}
                  showProfile={showProfile}
                  setSidebarOpen={setShowSidebar}
                  modernShell={useModernExploreShell}
                >
                  <SupabaseClaimTool user={user} />
                </PageLayout>
              )}
            />
          ) : null}
          <Route
            path="/gear-deals"
            element={gatedRouteElement(
              "/gear-deals",
              <PageLayout
                showUserProfile={showUserProfile}
                showProfile={showProfile}
                setSidebarOpen={setShowSidebar}
                enableScrollToTop={true}
                modernShell={useModernExploreShell}
              >
                <GearDeals currentUser={user} />
              </PageLayout>,
              allowGearDealsRoute
            )}
          />
          <Route
            path="/legal/terms"
            element={
              <PageLayout
                showUserProfile={showUserProfile}
                showProfile={showProfile}
                setSidebarOpen={setShowSidebar}
                modernShell={useModernExploreShell}
              >
                <LegalDocumentPage documentKey="terms" fallbackPath="/" />
              </PageLayout>
            }
          />
          <Route
            path="/legal/privacy"
            element={
              <PageLayout
                showUserProfile={showUserProfile}
                showProfile={showProfile}
                setSidebarOpen={setShowSidebar}
                modernShell={useModernExploreShell}
              >
                <LegalDocumentPage documentKey="privacy" fallbackPath="/" />
              </PageLayout>
            }
          />
          <Route
            path="/legal/account-deletion"
            element={
              <PageLayout
                showUserProfile={showUserProfile}
                showProfile={showProfile}
                setSidebarOpen={setShowSidebar}
                modernShell={useModernExploreShell}
              >
                <LegalDocumentPage documentKey="accountDeletion" fallbackPath="/" />
              </PageLayout>
            }
          />
          <Route
            path="/legal/attributions"
            element={
              <PageLayout
                showUserProfile={showUserProfile}
                showProfile={showProfile}
                setSidebarOpen={setShowSidebar}
                modernShell={useModernExploreShell}
              >
                <LegalDocumentPage documentKey="attributions" fallbackPath="/" />
              </PageLayout>
            }
          />
          <Route
            path="/messages"
            element={
              <PageLayout
                showUserProfile={showUserProfile}
                showProfile={showProfile}
                setSidebarOpen={setShowSidebar}
                modernShell={useModernExploreShell}
              >
                <div className="messages-view-container">
                  <div
                    className={`video-ui-container ${
                      messagesBackgroundMode === "white"
                        ? "white-background-mode"
                        : ""
                    }`}
                  >
                    {(!chatUserId || isMobileMessagesViewport)
                      ? messagesSettingsControl
                      : null}

                    {messagesBackgroundMode === "video" && (
                      <video
                        className="bg-video"
                        autoPlay
                        loop
                        muted
                        playsInline
                      >
                        <source src={bgVideo} type="video/mp4" />
                      </video>
                    )}

                    {messagesBackgroundMode === "video" && (
                      <div className="bg-overlay"></div>
                    )}

                    <div className="video-screen-content">
                      {!chatUserId ? (
                        <Inbox
                          currentUser={user}
                          onOpenChat={(userId) => setChatUserId(userId)}
                        />
                      ) : (
                        <Chat
                          currentUser={user}
                          userId={chatUserId}
                          userName={pendingChatUserName}
                          sharedText={pendingChatText}
                          onOpenSharedPost={postsActions.handleOpenSharedPostFromChat}
                          hideBackButton={showProfile || showUserProfile}
                          headerActions={
                            !isMobileMessagesViewport
                              ? messagesSettingsControl
                              : null
                          }
                          onBack={handleCloseChat}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </PageLayout>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
  );
}
